'use strict';

import { SystemStorageType } from '../../../App/model/system-storage-types';

/**
 * @ngdoc service
 * @name mx.SolutionBuilderAgent.DashboardWidgetDataService
 * @module mx.SolutionBuilderAgent
 *
 * @description
 * A service provides methods for retrieving data sources
 */
DashboardWidgetDataService.$inject = ['$q', '$m42DashboardManagerService', 'mx.shell.DataStorage'];
export default function DashboardWidgetDataService($q, $m42DashboardManagerService, dataStorage) {
	var self = this;

	var localStorage = dataStorage.getStorage(SystemStorageType.LocalStorage);
	var storageDashboardKey = 'm42Dashboards';
	var widgetsContent = {};
	var publicFunctions = {};
	const widgetsAvailabilityState = {};

	self.loadDashboardWidgets = loadDashboardWidgets;
	self.saveDashboardWidgets = saveDashboardWidgets;
	self.getWidgetContent = getWidgetContent;
	self.enableDashboardEditMode = enableDashboardEditMode;
	self.disableDashboardEditMode = disableDashboardEditMode;
	self.isDashboardEditable = isDashboardEditable;
	self.addWidgetToDashboard = addWidgetToDashboard;
	self.removeWidgetFromDashboard = removeWidgetFromDashboard;

	self.getDashboardWidgets = getDashboardWidgets;
	self.setDashboardWidgets = setDashboardWidgets;

	self.getAvailableDashboardWidgets = getAvailableDashboardWidgets;
	self.isWidgetAvailable = isWidgetAvailable;

	self.getDashboardLayout = getDashboardLayout;
	self.setDashboardLayout = setDashboardLayout;
	self.getDefaultDashboardLayout = getDefaultDashboardLayout;
	self.setDefaultDashboardLayout = setDefaultDashboardLayout;
	self.loadDashboardLayout = loadDashboardLayout;
	self.storeDashboardLayout = storeDashboardLayout;
	self.isDashboardCustomized = isDashboardCustomized;
	self.resetToDefault = resetToDefault;
	self.revertToCustomLayout = revertToCustomLayout;
	self.isCustomLayoutSaved = isCustomLayoutSaved;
	self.removeSavedCustomLayout = removeSavedCustomLayout;
	self.renderDashboard = renderDashboard;
	self.initDashboard = initDashboard;

	/* Function is necessary for m42Dashboard, must return promise */
	function loadDashboardWidgets(dashboardId) {
		var dfd = $q.defer();

		var widgets = getAvailableDashboardWidgets(dashboardId).filter(function (widget) {
			return widget.added;
		});

		dfd.resolve(widgets);

		return dfd.promise;
	}

	/* Function is necessary for m42Dashboard, must return promise */
	function saveDashboardWidgets(dashboardId, dashboardWidgets) {
		var dfd = $q.defer();

		var widgets = getDashboardWidgets(dashboardId);

		widgets.forEach(function (widget) {
			widget.added = typeof widget.added === 'boolean' ? widget.added : isWidgetInArray(widget.id, dashboardWidgets);
		});

		setDashboardWidgets(dashboardId, widgets);

		dfd.resolve();

		return dfd.promise;
	}

	/* Function is necessary for m42Dashboard, must return promise */
	function loadDashboardLayout(dashboardId, deviceType) {
		return $q.when(getDashboardLayout(dashboardId, deviceType));
	}

	/* Function is necessary for m42Dashboard, must return promise */
	function storeDashboardLayout(dashboardId, newLayouts, deviceType) {
		var dfd = $q.defer();

		var dashboardLayout = getDashboardLayout(dashboardId);

		dashboardLayout.forEach(function (layout) {
			newLayouts.some(function (newLayout) {
				if (newLayout.id === layout.id) {
					layout.frame = newLayout.frame;
					return true;
				}
			});
		});

		setDashboardLayout(dashboardId, dashboardLayout, deviceType);
		dfd.resolve();
		return dfd.promise;
	}

	/** **************************************************************/

	function getCurrentDeviceType() {
		return 'desktop';
	}

	function isWidgetInArray(widgetId, dashboardWidgets) {
		return dashboardWidgets.some(function (widget) {
			return widget.id === widgetId;
		});
	}

	function getDashboardsDataFromStorage() {
		var localDashboards = localStorage.get(storageDashboardKey);

		if (!localDashboards) {
			localDashboards = {};
		} else {
			localDashboards = JSON.parse(localDashboards);
			if (typeof localDashboards !== 'object' || localDashboards instanceof Array) {
				localDashboards = {};
			}
		}

		return localDashboards;
	}

	function saveDashboardsDataToStorage(data) {
		localStorage.set(storageDashboardKey, JSON.stringify(data));
	}

	function getDashboardData(dashboardId) {
		return getDashboardsDataFromStorage()[dashboardId] || {};
	}

	function setDashboardData(dashboardId, data) {
		var dashboards = getDashboardsDataFromStorage();

		dashboards[dashboardId] = data;

		saveDashboardsDataToStorage(dashboards);
	}

	function getDashboardWidgets(dashboardId) {
		return getDashboardData(dashboardId).widgets || [];
	}

	function getAvailableDashboardWidgets(dashboardId) {
		return getDashboardWidgets(dashboardId).filter((widget) => widgetsAvailabilityState[dashboardId][widget.id].available);
	}

	function isWidgetAvailable(dashboardId, widgetId) {
		return widgetsAvailabilityState[dashboardId][widgetId].available;
	}

	function setDashboardWidgets(dashboardId, widgets) {
		var dashboard = getDashboardData(dashboardId);

		dashboard.widgets = widgets;

		setDashboardData(dashboardId, dashboard);
	}

	function getWidgetContent(dashboardId, widgetId) {
		return widgetsContent[dashboardId] ? widgetsContent[dashboardId][widgetId] || '' : '';
	}

	function enableDashboardEditMode(dashboardId) {
		var dashboard = getDashboardData(dashboardId);

		dashboard.editMode = true;
		setDashboardData(dashboardId, dashboard);
	}

	function disableDashboardEditMode(dashboardId) {
		var dashboard = getDashboardData(dashboardId);

		dashboard.editMode = false;
		setDashboardData(dashboardId, dashboard);
	}

	function isDashboardEditable(dashboardId) {
		var dashboard = getDashboardData(dashboardId);

		return dashboard.editMode || false;
	}

	function updateDashboardLayout(dashboardId) {
		var widgetLayouts = getDashboardLayout(dashboardId);
		var widgets = getDashboardWidgets(dashboardId);

		widgets.forEach(function (widget) {
			var isFinded = widgetLayouts.some(function (widgetLayout) {
				if (widget.id === widgetLayout.id) {
					widgetLayout.added = widget.added;
					return true;
				}
			});

			if (!isFinded) {
				widgetLayouts.push({
					id: widget.id,
					frame: getDefaultLayoutForWidget(dashboardId, widget.id),
					added: widget.added,
				});
			}
		});

		setDashboardLayout(dashboardId, widgetLayouts);
	}

	function addWidgetToDashboard(dashboardId, widgetId, addAsAvailable = false) {
		var widgets = getDashboardWidgets(dashboardId);

		var defaultLayout = getDefaultFrame();

		widgets.some(function (widget) {
			if (widget.id === widgetId) {
				if ((addAsAvailable && !widgetsAvailabilityState[dashboardId][widgetId].available) || (!addAsAvailable && !widget.added)) {
					defaultLayout = getDefaultLayoutForWidget(dashboardId, widget.id);
					const currentWidgetLayout = getDashboardLayout(dashboardId).find((layout) => layout.id === widgetId)?.frame || {};

					var w = angular.extend({}, widget, {
						sizeX: typeof currentWidgetLayout.size.w !== 'undefined' ? currentWidgetLayout.size.w : defaultLayout.size.w,
						sizeY: typeof currentWidgetLayout.size.h !== 'undefined' ? currentWidgetLayout.size.h : defaultLayout.size.h,
						row: typeof currentWidgetLayout.origin.y !== 'undefined' ? currentWidgetLayout.origin.y : defaultLayout.origin.y,
						col: typeof currentWidgetLayout.origin.x !== 'undefined' ? currentWidgetLayout.origin.x : defaultLayout.origin.x,
					});

					if (addAsAvailable) {
						widgetsAvailabilityState[dashboardId][widgetId].available = true;
					} else {
						widget.added = true;
					}

					if (widget.added && widgetsAvailabilityState[dashboardId][widgetId].available) {
						$m42DashboardManagerService.addWidgetToDashboard(dashboardId, w);
					}
				}
				return true;
			}
		});

		setDashboardWidgets(dashboardId, widgets);

		if (!addAsAvailable) {
			updateDashboardLayout(dashboardId);
		}
	}

	function removeWidgetFromDashboard(dashboardId, widgetId, removeAsUnavailable = false) {
		var widgets = getDashboardWidgets(dashboardId);

		widgets.some(function (widget) {
			if (widget.id === widgetId) {
				if (removeAsUnavailable && widgetsAvailabilityState[dashboardId][widgetId].available) {
					widgetsAvailabilityState[dashboardId][widgetId].available = false;
					publicFunctions[dashboardId].deleteWidgetById?.(widgetId);
				} else if (widget.added) {
					widget.added = false;
				}
				return true;
			}
		});

		setDashboardWidgets(dashboardId, widgets);

		if (!removeAsUnavailable) {
			updateDashboardLayout(dashboardId);
		}
	}

	function getDashboardLayout(dashboardId, deviceType) {
		deviceType = getCurrentDeviceType();

		return getDashboardData(dashboardId)[deviceType] || [];
	}

	function setDashboardLayout(dashboardId, newLayout, deviceType) {
		deviceType = getCurrentDeviceType();

		var dashboard = getDashboardData(dashboardId);

		if (typeof dashboard[deviceType] === 'undefined') {
			dashboard.isCustomized = false;
		} else {
			dashboard.isCustomized = !angular.equals(newLayout, getDefaultDashboardLayout(dashboardId));
		}

		dashboard[deviceType] = newLayout;

		setDashboardData(dashboardId, dashboard);
	}

	function getDefaultDashboardLayout(dashboardId) {
		return getDashboardData(dashboardId).defaultLayout || [];
	}

	function setDefaultDashboardLayout(dashboardId, layout) {
		var dashboard = getDashboardData(dashboardId);

		dashboard.defaultLayout = layout;
		setDashboardData(dashboardId, dashboard);
	}

	function getDefaultFrame() {
		return {
			origin: {
				y: 0,
				x: 0,
			},
			size: {
				w: 5,
				h: 3,
			},
		};
	}

	function getDefaultLayoutForWidget(dashboardId, widgetId) {
		var layout = getDefaultFrame();

		getDashboardData(dashboardId).defaultLayout.some(function (defaultWidgetLayout) {
			if (defaultWidgetLayout.id === widgetId) {
				layout = defaultWidgetLayout.frame;
				return true;
			}
		});

		return layout;
	}

	function isDashboardCustomized(dashboardId) {
		return Boolean(getDashboardData(dashboardId).isCustomized);
	}

	function resetToDefault(dashboardId) {
		var defaultDashboardLayout = getDefaultDashboardLayout(dashboardId);

		if (!defaultDashboardLayout) {
			return;
		}

		var deviceType = getCurrentDeviceType();

		var customLayout = getDashboardLayout(dashboardId, deviceType);

		setDashboardLayout(dashboardId, defaultDashboardLayout, deviceType);

		var dashboard = getDashboardData(dashboardId);

		dashboard.isCustomized = false;
		dashboard.customLayout = customLayout;

		if (dashboard.widgets) {
			dashboard.widgets.forEach(function (widget) {
				widget.added = defaultDashboardLayout.some(function (layout) {
					return layout.id === widget.id && layout.added;
				});
			});
		}

		setDashboardData(dashboardId, dashboard);
	}

	function isCustomLayoutSaved(dashboardId) {
		return Boolean(getDashboardData(dashboardId).customLayout);
	}

	function removeSavedCustomLayout(dashboardId) {
		var dashboard = getDashboardData(dashboardId);

		delete dashboard.customLayout;
		setDashboardData(dashboardId, dashboard);
	}

	function revertToCustomLayout(dashboardId) {
		var dashboard = getDashboardData(dashboardId);

		if (!dashboard.customLayout) {
			return;
		}
		var deviceType = getCurrentDeviceType();

		dashboard.isCustomized = true;
		dashboard[deviceType] = dashboard.customLayout;

		if (dashboard.widgets) {
			dashboard.widgets.forEach(function (widget) {
				widget.added = dashboard.customLayout.some(function (layout) {
					return layout.id === widget.id && layout.added;
				});
			});
		}

		setDashboardData(dashboardId, dashboard);
	}

	function renderDashboard(dashboardId) {
		if (publicFunctions && publicFunctions[dashboardId] && typeof publicFunctions[dashboardId].renderDashboard === 'function') {
			publicFunctions[dashboardId].renderDashboard();
		}
	}

	function initDashboard(dashboardId, widgets, defaultLayout, dashboardPublicFunctions) {
		disableDashboardEditMode(dashboardId);

		var isCustomized = isDashboardCustomized(dashboardId),
			customizedLayout;

		if (isCustomized) {
			customizedLayout = getDashboardLayout(dashboardId);
		}

		var prevDefaultLayout = getDefaultDashboardLayout(dashboardId);

		var isDefaultLayoutChanged = !angular.equals(prevDefaultLayout, defaultLayout);
		var isSetOfWidgetsChanged = typeof prevDefaultLayout !== 'undefined' && prevDefaultLayout.length !== defaultLayout.length;

		if (isDefaultLayoutChanged) {
			setDefaultDashboardLayout(dashboardId, defaultLayout);
		}

		widgetsContent[dashboardId] = {};
		widgetsAvailabilityState[dashboardId] = {};
		publicFunctions[dashboardId] = dashboardPublicFunctions;

		widgets.forEach(function (widget) {
			widgetsContent[dashboardId][widget.id] = widget.content;
			delete widget.content;

			widgetsAvailabilityState[dashboardId][widget.id] = { available: widget.available };
			delete widget.available;

			if (isCustomized) {
				widget.added = customizedLayout.some(function (customWidget) {
					return customWidget.id === widget.id && customWidget.added;
				});
			}
		});

		setDashboardWidgets(dashboardId, widgets);

		if (!isCustomized && isDefaultLayoutChanged) {
			setDashboardLayout(dashboardId, defaultLayout);
		} else if (isSetOfWidgetsChanged) {
			defaultLayout.forEach(function (layout) {
				layout.added = false;
				customizedLayout.some(function (customLayout) {
					if (customLayout.id === layout.id) {
						layout.added = customLayout.added;
						layout.frame = customLayout.frame;
						return true;
					}
				});
			});

			setDashboardLayout(dashboardId, defaultLayout);

			if (isCustomLayoutSaved(dashboardId)) {
				removeSavedCustomLayout(dashboardId);
			}
		}

		return {
			isCustomized: isCustomized,
			isSetOfWidgetsChanged: isSetOfWidgetsChanged,
			isDefaultLayoutChanged: isDefaultLayoutChanged,
		};
	}
}
